/**
 * GridCrash — The Gauntlet (6D)
 *
 * Push-your-luck roguelike descent: one life, chained floors that grow
 * bigger and denser, and a run pot of unbanked loot. After each cleared
 * floor you either bank the pot into your wallet or descend — which
 * multiplies the pot but risks losing it all to a single mine. Relics
 * bought with pot coins tilt the odds.
 *
 * The active run lives in memory (refresh/abandon forfeits the pot — one
 * life is the contract). Only lifetime stats persist, offline-first like
 * quests.ts.
 */

import { DIFFICULTIES, type DifficultyConfig } from "./engine.js";
import { creditBonus } from "./wallet.js";

// ─── Relics ───────────────────────────────────────────────────────────────────

export interface Relic {
  id: string;
  emoji: string;
  name: string;
  description: string;
  /** Price in run-pot coins (not wallet coins). */
  price: number;
}

export const RELICS: Relic[] = [
  {
    id: "seismograph",
    emoji: "🧿",
    name: "Seismograph",
    description: "Absorbs one mine tap per run — the run survives",
    price: 120,
  },
  {
    id: "dowsing_rod",
    emoji: "🪄",
    name: "Dowsing Rod",
    description: "First hint each floor is free",
    price: 80,
  },
  {
    id: "hourglass",
    emoji: "⏳",
    name: "Hourglass",
    description: "+30 seconds on every timed floor",
    price: 60,
  },
  {
    id: "lodestone",
    emoji: "🧲",
    name: "Lodestone",
    description: "Floor loot +25%",
    price: 100,
  },
];

export function getRelic(id: string): Relic | undefined {
  return RELICS.find((r) => r.id === id);
}

// ─── Run state (in-memory — one life, no resume) ─────────────────────────────

export interface GauntletRun {
  floor: number;
  pot: number;
  relics: string[];
  seismographUsed: boolean;
  dowsingUsedThisFloor: boolean;
}

let run: GauntletRun | null = null;

export function isRunActive(): boolean {
  return run !== null;
}

export function getRun(): Readonly<GauntletRun> | null {
  return run;
}

export function startRun(): GauntletRun {
  run = {
    floor: 1,
    pot: 0,
    relics: [],
    seismographUsed: false,
    dowsingUsedThisFloor: false,
  };
  return run;
}

export function hasRelic(id: string): boolean {
  return run?.relics.includes(id) ?? false;
}

/** Buy a relic with pot coins at an intermission. */
export function buyRelic(id: string): boolean {
  const relic = getRelic(id);
  if (!run || !relic || hasRelic(id) || run.pot < relic.price) return false;
  run.pot -= relic.price;
  run.relics.push(id);
  return true;
}

/** Seismograph: can it absorb a mine right now? */
export function canSeismographSave(): boolean {
  return run !== null && hasRelic("seismograph") && !run.seismographUsed;
}

export function markSeismographUsed(): void {
  if (run) run.seismographUsed = true;
}

/** Dowsing Rod: is the free floor hint still available? */
export function dowsingAvailable(): boolean {
  return run !== null && hasRelic("dowsing_rod") && !run.dowsingUsedThisFloor;
}

export function markDowsingUsed(): void {
  if (run) run.dowsingUsedThisFloor = true;
}

// ─── Floor generation ─────────────────────────────────────────────────────────

/** First floor with a countdown clock. */
export const FIRST_TIMED_FLOOR = 8;

/**
 * Difficulty for a floor: 6×6 with 4 mines at floor 1, growing to a
 * 16×16 at ~22% density deep down. Floors 8+ are timed. The id maps to
 * the closest standard tier so achievements and quests treat the floor
 * like that tier.
 */
export function floorConfig(floor: number): DifficultyConfig {
  const side = Math.min(16, 5 + floor);
  const cells = side * side;
  const density = Math.min(0.22, 0.10 + floor * 0.01);
  const mineCount = Math.max(4, Math.round(cells * density));

  const tier = cells <= 81 ? DIFFICULTIES["easy"]
             : cells <= 169 ? DIFFICULTIES["medium"]
             : DIFFICULTIES["hard"];

  let timeLimitSecs: number | undefined;
  if (floor >= FIRST_TIMED_FLOOR) {
    timeLimitSecs = Math.round((cells - mineCount) * 1.6);
    if (hasRelic("hourglass")) timeLimitSecs += 30;
  }

  return {
    ...tier,
    label: `Floor ${floor}`,
    rows: side,
    cols: side,
    mineCount,
    ...(timeLimitSecs !== undefined ? { timeLimitSecs } : {}),
  };
}

/** Loot paid into the pot for clearing a floor. */
export function floorLoot(floor: number): number {
  const base = 20 + floor * 10;
  return hasRelic("lodestone") ? Math.round(base * 1.25) : base;
}

// ─── Run transitions ──────────────────────────────────────────────────────────

/** Credit the cleared floor's loot into the pot. Returns the loot amount. */
export function addFloorLoot(): number {
  if (!run) return 0;
  const loot = floorLoot(run.floor);
  run.pot += loot;
  return loot;
}

/** Descend: pot ×1.25, next floor, floor-scoped relic charges reset. */
export function descend(): void {
  if (!run) return;
  run.pot = Math.round(run.pot * 1.25);
  run.floor += 1;
  run.dowsingUsedThisFloor = false;
}

/** Bank & escape: pot goes to the wallet, run ends. Returns coins banked. */
export function bankRun(): number {
  if (!run) return 0;
  const banked = run.pot;
  if (banked > 0) creditBonus(banked);
  recordRunEnd(run.floor, banked);
  run = null;
  return banked;
}

/** Mine hit / clock out: the pot is lost, run ends. */
export function bustRun(): void {
  if (!run) return;
  recordRunEnd(run.floor, 0);
  run = null;
}

/** Abandoning (new game / other mode) forfeits the run silently. */
export function abandonRun(): void {
  run = null;
}

// ─── Persistent stats ─────────────────────────────────────────────────────────

const KEY = "gridcrash.gauntlet.v1";

export interface GauntletStats {
  bestFloor: number;
  biggestBank: number;
  runs: number;
}

let memStats: GauntletStats | null = null;

export function getStats(): GauntletStats {
  if (memStats) return memStats;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<GauntletStats>;
      memStats = {
        bestFloor: typeof parsed.bestFloor === "number" ? parsed.bestFloor : 0,
        biggestBank: typeof parsed.biggestBank === "number" ? parsed.biggestBank : 0,
        runs: typeof parsed.runs === "number" ? parsed.runs : 0,
      };
      return memStats;
    }
  } catch { /* corrupted or private browsing — start fresh */ }
  memStats = { bestFloor: 0, biggestBank: 0, runs: 0 };
  return memStats;
}

function recordRunEnd(floorReached: number, banked: number): void {
  const stats = getStats();
  memStats = {
    bestFloor: Math.max(stats.bestFloor, floorReached),
    biggestBank: Math.max(stats.biggestBank, banked),
    runs: stats.runs + 1,
  };
  try { localStorage.setItem(KEY, JSON.stringify(memStats)); } catch { /* ignore */ }
}
